/**
 * The chat's side of the definitions cards: turns each learned item on a
 * message into its own kit `DefinitionCard`, calls the knowledge API when a
 * button is pressed, and writes each returned item back onto the message so
 * the card keeps its state while the chat is open.
 *
 * Also maps "definitions used" onto the kit's `ChatDefinitionsNote`.
 */
import React from 'react';
import { Box } from '@mui/material';
import {
  ChatDefinitionsNote,
  DefinitionCard,
  type ChatDefinitionsNoteItem,
  type DefinitionCardError,
  type DefinitionCardItem,
  type DefinitionSaveRequest,
} from '../../../ui-kit';
import { useChatStoreApi } from '../../../chat/core/ChatStoreContext';
import type { CopilotMessage } from '../stores/useCopilotStore';
import type { DefinitionUsed, LearnedItem } from '../../../types/definitions';
import {
  DefinitionRequestError,
  deleteLearnedDefinition,
  dontSaveLearnedDefinition,
  editLearnedDefinition,
  restoreLearnedDefinition,
  saveLearnedDefinition,
} from '../../../services/definitionsService';

/** Verdicts where saving should replace an existing chat-saved definition. */
const UPDATE_VERDICTS = new Set(['same_as_saved', 'differs_from_saved']);

export function toCardItem(item: LearnedItem): DefinitionCardItem {
  const match = item.check?.match;
  return {
    id: item.item_id,
    name: item.name,
    definition: item.definition,
    state: item.state,
    updateTarget:
      match && item.check && UPDATE_VERDICTS.has(item.check.verdict)
        ? { id: match.node_id, name: match.name }
        : null,
    note: item.check?.note || null,
    savedBy: item.saved_by?.name ?? null,
    savedAt: item.saved_at ?? null,
    updatedBy: item.updated_by?.name ?? null,
    updatedAt: item.updated_at ?? null,
  };
}

export function toNoteItem(used: DefinitionUsed): ChatDefinitionsNoteItem {
  const kind =
    used.definition_type === 'this_chat' ? 'this_chat' : used.definition_type === 'chat' ? 'saved' : 'official';
  return { name: used.name, kind, where: used.board ?? null, applied: used.applied ?? null };
}

export function DefinitionsUsedNote({ items }: { items: DefinitionUsed[] }) {
  return <ChatDefinitionsNote items={items.map(toNoteItem)} />;
}

export function LearnedDefinitions({ message, readOnly = false }: { message: CopilotMessage; readOnly?: boolean }) {
  const store = useChatStoreApi();
  const items = message.definitionItems ?? [];
  const eventId = message.definitionsEventId;
  const [busy, setBusy] = React.useState<string[]>([]);
  const [errors, setErrors] = React.useState<Record<string, DefinitionCardError | null>>({});

  const run = React.useCallback(
    async (itemId: string, call: (eventId: string) => Promise<LearnedItem>) => {
      if (!eventId) return;
      setBusy((b) => [...b, itemId]);
      setErrors((e) => ({ ...e, [itemId]: null }));
      try {
        const next = await call(eventId);
        // Read the message fresh: another card may have changed it meanwhile.
        const s = store.getState();
        const current = s.messages.find((m) => m.id === message.id)?.definitionItems ?? message.definitionItems ?? [];
        s.updateMessage(message.id, {
          definitionItems: current.map((i) => (i.item_id === itemId ? { ...i, ...next } : i)),
        });
      } catch (err) {
        const dup = err instanceof DefinitionRequestError ? err.duplicate : undefined;
        setErrors((e) => ({
          ...e,
          [itemId]: {
            message: err instanceof Error && err.message ? err.message : 'Something went wrong. Please try again.',
            duplicateOf: dup ? { id: dup.node_id, name: dup.name } : null,
          },
        }));
      } finally {
        setBusy((b) => b.filter((id) => id !== itemId));
      }
    },
    [eventId, message.id, message.definitionItems, store],
  );

  if (items.length === 0) return null;

  const onSave = (itemId: string, r: DefinitionSaveRequest) =>
    run(itemId, (ev) =>
      saveLearnedDefinition(ev, itemId, {
        name: r.name,
        definition: r.definition,
        mode: r.mode,
        ...(r.targetNodeId ? { target_node_id: r.targetNodeId } : {}),
      }),
    );

  // One card per definition, stacked: each is decided on its own.
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {items.map((item) => (
        <DefinitionCard
          key={item.item_id}
          item={toCardItem(item)}
          busy={busy.includes(item.item_id)}
          error={errors[item.item_id]}
          readOnly={readOnly || !eventId}
          onSave={(r) => onSave(item.item_id, r)}
          onDontSave={() => run(item.item_id, (ev) => dontSaveLearnedDefinition(ev, item.item_id))}
          onEdit={(change) => run(item.item_id, (ev) => editLearnedDefinition(ev, item.item_id, change))}
          onDelete={() => run(item.item_id, (ev) => deleteLearnedDefinition(ev, item.item_id))}
          onRestore={() => run(item.item_id, (ev) => restoreLearnedDefinition(ev, item.item_id))}
        />
      ))}
    </Box>
  );
}
